import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux'
import { useParams, Link, useSearchParams } from 'react-router-dom'
import Nestable from 'react-nestable'
import Dropdown from 'react-bootstrap/Dropdown'

import { setProject, setIsDeleted } from '../obj/pscState'

import ErrorMessage from '../components/psc/lib/ErrorMessage'
import TextField from '../components/psc/lib/TextField'
import TextArea from '../components/psc/lib/TextArea'
import UserSelect from '../components/psc/lib/UserSelect'
import SingleSelect from '../components/psc/lib/SingleSelect'
import MultiSelect from '../components/psc/lib/MultiSelect'
import CancelButton from '../components/psc/lib/CancelButton'
import SaveButton from '../components/psc/lib/SaveButton';
import DeleteButton from '../components/psc/lib/DeleteButton'
import InfoModal from '../components/psc/lib/InfoModal'
import AsyncContentModal from '../components/psc/lib/AsyncContentModal'
import Confirm from '../components/psc/lib/Confirm'
import Loading from '../components/psc/lib/Loading'
import Checkbox from '../components/psc/lib/Checkbox'

import useAuthentication from '../hooks/useAuthentication';
import useProjectTypes from '../hooks/useProjectTypes';
import useUserManagement from '../hooks/useUserManagement';
import useUsergroupManagement from '../hooks/useUsergroupManagement';
import useProjectList from '../hooks/useProjectList';
import useProjectContents from '../hooks/useProjectContents';
import useGncHistory from '../hooks/useGncHistory'

const Project = () => {
    const [searchParams] = useSearchParams()
    const productTreeRef = useRef(null)
    const dispatch = useDispatch()
    const pscState = useSelector(state => state.pscState.value)
    const config = pscState.config || {}
    const params = useParams()
    const isNew = params.projectID === 'new'
    const projectID = isNew ? params.projectID : Number(params.projectID)
    const projectURL = isNew ? '/v0/projects' : '/v0/projects/' + projectID

    const {
        currentUser,
        updateUser: updateCurrentUser,
    } = useAuthentication();

    const {
        getProjectType,
    } = useProjectTypes()
    const {
        activeUsers,
        updateUser,
        updateAllUsers,
        saveUserUpdates,
        userHasRole,
        userHasExplicitProjectAccess,
        userProjectAccessLevel,
        forceReloadUsers,
    } = useUserManagement()
    
    const {
        usergroups,
        updateUsergroup,
        reloadUsergroups,
        usergroupProjectAccessLevel,
    } = useUsergroupManagement()
    const {
        projectUpdate,
        projectDelete,
        reloadProjects,
    } = useProjectList()
    const {
        reloadProjectContents,
    } = useProjectContents()
    
    const {
        goBack,
    } = useGncHistory()
    
    // Project fields
    const [id, setID] = useState(null)
    const [updatedAt, setUpdatedAt] = useState(null)
    const [name, setName] = useState('')
    const [projectCode, setProjectCode] = useState('')
    const [organisationCode, setOrganisationCode] = useState(config.projects_default_organisation_code || 'GNC')
    const [elementCode, setElementCode] = useState('')
    const [projectStatusID, setProjectStatusID] = useState(1)
    const [description, setDescription] = useState('')
    const [projectManagerUserID, setProjectManagerUserID] = useState(null)
    const [paManagerUserID, setPaManagerUserID] = useState(null)
    const [technicalManagerUserID, setTechnicalManagerUserID] = useState(null)
    const [bidManagerUserID, setBidManagerUserID] = useState(null)
    const [projectSystems, setProjectSystems] = useState([])
    const [doAllowDocumentSharing, setDoAllowDocumentSharing] = useState(false)
    const [isArchived, setIsArchived] = useState(false)
    const [projectConfigurationSchemeID, setProjectConfigurationSchemeID] = useState(null)

    // Project fields, OSS specific
    const [customer, setCustomer] = useState('')
    const [fundingSource, setFundingSource] = useState('')
    const [fundingTypeID, setFundingTypeID] = useState(null)
    const [fundingLevel, setFundingLevel] = useState('')
    const [product, setProduct] = useState('')
    const [totalValue, setTotalValue] = useState('')

    // Read-only
    const [hasLoaded, setHasLoaded] = useState(false)
    const [isUsed, setIsUsed] = useState(false)
    const [projectTypeID, setProjectTypeID] = useState(null)
    const [incorporatedInProjects, setIncorporatedInProjects] = useState([])
    const [error, setError] = useState(false)
    const [submitActive, setSubmitActive] = useState(false)
    const [submitSpinner, setSubmitSpinner] = useState(false)
    const [projectAccessLevels, setProjectAccessLevels] = useState([])
    const [projectStatuses, setProjectStatuses] = useState([])
    const [projectConfigurationSchemes, setProjectConfigurationSchemes] = useState([])
    const [projectFundingTypes, setProjectFundingTypes] = useState([])
    const [models, setModels] = useState([])
    // TODO: Is this needed? setCollapseKey is never used...
    const [collapseKey, setCollapseKey] = useState('1')
    const [showConvertingModal, setShowConvertingModal] = useState(false)

    const hasRoleRequiredForAdminEdit = useMemo(() => {
        switch(projectTypeID) {
            case 1: // Project
                return userHasRole(currentUser, 'project_admin')
            case 2: // Opportunity
                return userHasRole(currentUser, 'opportunity_admin')
            case 3: // Product
                return userHasRole(currentUser, 'product_admin')
            default:
                return false
        }
    }, [currentUser, projectTypeID, userHasRole])
    
    const hasRoleRequiredForEdit = useMemo(() => {
        switch(projectTypeID) {
            case 1: // Project
                return userHasRole(currentUser, 'project_controller')
            case 2: // Opportunity
                return userHasRole(currentUser, 'opportunity_admin')
            case 3: // Product
                return userHasRole(currentUser, 'product_controller')
            default:
                return false
        }
    }, [currentUser, projectTypeID, userHasRole])

    const projectType = useMemo(() => getProjectType(projectTypeID), [ projectTypeID, getProjectType ])

    const addAllUsers = useCallback((levelId) => {
        const updatedUsers = activeUsers.map(user => ({
            ...user,
            user_project_access: [
                ...user.user_project_access.filter(access => access.project_id !== projectID),
                {
                    user_id: user.id,
                    project_id: projectID,
                    user_project_access_level_id: levelId
                }
            ]
        }))
        updateAllUsers(updatedUsers)
    }, [activeUsers, projectID, updateAllUsers])

    const removeAllUsers = useCallback(() => {
        const updatedUsers = activeUsers.map(user => ({
            ...user,
            user_project_access: user.user_project_access
                .filter(access => access.project_id !== projectID)
        }))
        updateAllUsers(updatedUsers)
    }, [activeUsers, projectID, updateAllUsers])

    const addAllUsergroups = (level_id) => {
        usergroups.forEach(usergroup => {
            usergroup.usergroup_project_access.push({
                project_id: projectID,
                user_project_access_level_id: level_id
            })
            updateUsergroup(usergroup.id, usergroup)
        })
    }

    const removeAllUsergroups = () => {
        usergroups.forEach(usergroup => {
            usergroup.usergroup_project_access = usergroup.usergroup_project_access.filter(access => access.project_id !== projectID)
            updateUsergroup(usergroup.id, usergroup)
        })
    }

    useEffect(() => {
        if( productTreeRef.current !== null) {
            // could be a string "ALL"/ "NONE", or an array of ids...
            let collapsed = localStorage[collapseKey]
            try {
                collapsed = JSON.parse(collapsed)
            } catch(err) {
                console.log('Project: Failed to parse collapsed value: ' + err);
            }
            productTreeRef.current.collapse(collapsed)
        }
    }, [collapseKey, productTreeRef])

    useEffect(() => {
        fetch('/v0/users_project_access_levels', { credentials: 'same-origin' })
            .then(function(response) {
                return response.json()
            })
            .then(function(json) {
                setProjectAccessLevels(json.results)
            })
    }, [])
    
    useEffect(() => {
        if (projectTypeID) {
        // Collated: statuses, config schemes, funding types, models
        fetch('/v0/project_information', { credentials: 'same-origin' })
            .then(function(response) {
                return response.json()
            })
            .then(function(json) {
                const data = json.result
                setProjectStatuses(data.project_statuses.filter(item => {
                    return item.project_type_id === projectTypeID
                }))
                setProjectConfigurationSchemes(data.project_configuration_schemes)
                setProjectFundingTypes(data.project_funding_types)
                setModels(data.models)
                // Bit random to have this here, but on new Projects,
                // we do need this loaded before we start showing the form
                // We do at least need projectType to be set.
                setHasLoaded(true)
            })
        }
    }, [projectTypeID])

    useEffect(() => {
        if( isNew ) {
            const id = searchParams.get('project_type_id') ? Number(searchParams.get('project_type_id')) :  1
            setProjectTypeID(id)
            return
        }
    }, [isNew])

    useEffect(() => {
        const level = config?.create_project_user_access_default;
        const projectMappings = {
            'all_users_readonly': 1,
            'all_users_member': 2,
            'all_users_cadm_controller': 3
        };

        if (isNew && level) {
            addAllUsers(projectMappings[level]);
        }
    }, [isNew, config?.create_project_user_access_default])

    // Get ProjectConfigurationSchemeID from localStorage
    useEffect(() => {
        if( projectTypeID === null ) {
            return
        }
        const configSchemeKey = "projectConfigurationSchemeID_" + projectTypeID
        if (isNew
            && localStorage[configSchemeKey]
            && parseInt(localStorage[configSchemeKey], 10) > 0
            && projectConfigurationSchemeID === null ) {
            console.log("foo", configSchemeKey, localStorage[configSchemeKey])
            setProjectConfigurationSchemeID(parseInt(localStorage[configSchemeKey], 10))
            return
        }
    }, [isNew, projectTypeID, projectConfigurationSchemeID])
    
    useEffect(() => {
        if( isNew ) {
            return
        }
        
        fetch(projectURL, { credentials: 'same-origin' })
            .then(function(response) {
                return response.json()
            })
            .then(function(json) {
                let project = json.result
                if( !project ) {
                    return
                }
                setProjectTypeID(project.project_type_id)
                setID(project.id)
                setName(project.name)
                setProjectCode(project.project_code)
                setOrganisationCode(project.organisation_code)
                setElementCode(project.element_code)
                setProjectStatusID(project.project_status_id)
                setDescription(project.description)
                setProjectManagerUserID(project.project_manager_user_id)
                setPaManagerUserID(project.pa_manager_user_id)
                setTechnicalManagerUserID(project.technical_manager_user_id)
                setBidManagerUserID(project.bid_manager_user_id)
                setProjectSystems(project.project_systems)
                setProjectConfigurationSchemeID(project.project_configuration_scheme_id)
                setDoAllowDocumentSharing(project.do_allow_document_sharing)
                setIsArchived(project.is_archived)
                setIsUsed(project.is_used)
                setIncorporatedInProjects(project.incorporated_in_projects)
                
                // Project fields, OSS specific
                setCustomer(project.customer)
                setFundingSource(project.funding_source)
                setFundingTypeID(project.funding_type_id)
                setFundingLevel(project.funding_level)
                setProduct(project.product)
                setTotalValue(project.total_value)
                setUpdatedAt(project.updated_at)
            })
    }, [isNew, projectURL, searchParams])

    const selectedUsers = useMemo(() =>
        activeUsers.filter(user => userProjectAccessLevel(user, projectID) > 0)
    , [activeUsers, projectID, userProjectAccessLevel])

    const selectedUsergroups = useMemo(() =>
        usergroups.filter(usergroup => usergroupProjectAccessLevel(usergroup, projectID) > 0)
    , [usergroups, projectID, usergroupProjectAccessLevel])

    // Convert object to lowercase json
    const toBackend = () => {
        let data = {
            updated_at: updatedAt,
            name: name,
            project_type_id: projectTypeID,
            project_code: projectCode,
            organisation_code: organisationCode,
            element_code: elementCode,
            project_status_id: projectStatusID,
            description: description,
            project_manager_user_id: projectManagerUserID,
            pa_manager_user_id: paManagerUserID,
            technical_manager_user_id: technicalManagerUserID,
            bid_manager_user_id: bidManagerUserID,
            project_systems: projectSystems,
            project_configuration_scheme_id: projectConfigurationSchemeID,
            do_allow_document_sharing: doAllowDocumentSharing,
            customer: customer,
            funding_source: fundingSource,
            funding_type_id: fundingTypeID,
            funding_level: fundingLevel,
            product: product,
            total_value: totalValue,
        }
        if( !isNew ) {
            data.id = id
        }

        data.user_project_access = selectedUsers.map(user => {
            const level = user.user_project_access.find(access => access.project_id === projectID)?.user_project_access_level_id
            return {
                user_id: user.id,
                user_project_access_level_id: level
            }
        })
        data.usergroup_project_access = selectedUsergroups.map(usergroup => {
            const level = usergroup.usergroup_project_access.find(access => access.project_id === projectID)?.user_project_access_level_id
            return {
                usergroup_id: usergroup.id,
               user_project_access_level_id: level // wtf? should be usergroup.. but backend doesn't think so
            }
        })
        return data
    }

    const validateBeforeSubmit = () => {
        let errors = []

        if ( ! name ) {
            errors.push('Name is required')
        }

        if ( ! projectCode ) {
            errors.push('Code is required')
        }

        if ( ! organisationCode ) {
            errors.push('Organization Code is required')
        }

        if ( ! projectConfigurationSchemeID ) {
            errors.push('Configuration Scheme is required')
        }

        // skip this for now (in react upgrade)
//        if ( projectSystems.length > 1 ) {
//            // They need names
//            if( projectSystems.find(item => item.name === '') ) {
//                errors.push('Product Tree System name is required')
//            }
//        }

        if ( errors.length > 0 ) {
            setError(errors)
            return false
        }
        return true
    }

    const handleSubmitError = (props) => {
        setSubmitActive(false)
        setSubmitSpinner(false)
        setError([ props.message ])
    }

    const handleSubmit = (event) => {
        if( event ) {
            event.preventDefault()
        }
        if( submitActive || !validateBeforeSubmit() ) {
            return null
        }

        // Save project_configuration_scheme_id so it can be prepopualted next time
        const configSchemeKey = "projectConfigurationSchemeID_" + projectTypeID
        localStorage[configSchemeKey] = projectConfigurationSchemeID
        
        setSubmitActive(true)
        setSubmitSpinner(true)
        saveUserUpdates()

        const data = toBackend()
        let submit = {
            objType: 'projects',
            objID: projectID,
            data: data,
            navigateOverride: function() {
            },
            successCallback: (json) => {
                const projectData = json.result

                const currentUserProjectAccess = data.user_project_access
                    .find(user => user.user_id === currentUser.id)
                const updatedUser = currentUserProjectAccess ? { ...currentUser, user_project_access: currentUser.user_project_access.map(project =>
                    project.project_id === projectData.id ? {...project, user_project_access_level_id: currentUserProjectAccess.user_project_access_level_id} : project
                )} : null

                forceReloadUsers()
                reloadUsergroups()
                reloadProjectContents() // In case we've been added access, we need to reload all content
                dispatch(setProject(projectData))
                projectUpdate(projectData.id, projectData)
                goBack()
            }
        }
        
        return submit
    }

    const handleArchive = () => {
        let data = toBackend()
        data.is_archived = true
        data.user_project_access = []
        data.usergroup_project_access = []
        
        const userUrl = '/v0/users/'+currentUser.id
        
        fetch(projectURL, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        }).then(response => {
            if (response.ok) {
                return response.json()
            }
            throw new Error('Network response was not ok.');
        }).then(json => {
            projectUpdate(json.result.id, json.result)
            dispatch(setProject(json.result))
            reloadProjects()
            fetch(userUrl, { credentials: 'same-origin' })
                .then(function(response) {
                    return response.json()
                })
                .then(function(json) {
                    updateCurrentUser(json.result);
                })
            goBack()
        }).catch(error => {
            console.error('There has been a problem with your fetch operation:', error.message);
        });
    }
    
    const handleUnarchive = () => {
        let data = toBackend()
        data.is_archived = false
        
        fetch(projectURL, {
            method: 'PATCH',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        }).then(response => {
            if (response.ok) {
                return response.json()
            }
            throw new Error('Network response was not ok.');
        }).then(json => {
            projectUpdate(json.result.id, json.result)
            dispatch(setProject(json.result))
            reloadProjects()
            goBack()
        }).catch(error => {
            console.error('There has been a problem with your fetch operation:', error.message);
        });
    }

    /* Product Tree stuff START */
    const updateProductTreeNode = (node, id, newChild) => {
        if (hasRoleRequiredForEdit) {
            node.children.map((item) => updateProductTreeNode(item, id, newChild))

            if( node.id === id ) {
                node.children.unshift(newChild)
            }
            return node
        }
    }
    
    const addProductTreeRootNode = () => {
        if (hasRoleRequiredForEdit) {
            const name = 'New Top-Level Item'
            const newItem = { is_new: true, id: new Date().getTime(), name: name, project_system_models: [], children: [] }

            setProjectSystems([ ...projectSystems, newItem ])
        }
    }

    const addProductTreeNode = (node) => {
        if (hasRoleRequiredForEdit) {
            const name = 'New Item'
            const newItem = { is_new: true, id: new Date().getTime(), name: name, project_system_models: [], children: [] }

            // Find node in tree, update it, set
            const project_systems = projectSystems.map((item) => updateProductTreeNode(item, node.id, newItem))
            setProjectSystems(project_systems)
        }
    }

    const _editProductTreeNodeName = (node, id, newName) => ({
        ...node,
        name: node.id === id ? newName : node.name,
        children: node.children.map((item) => _editProductTreeNodeName(item, id, newName)),
    })
    
    const editProductTreeNodeName = (node, { target: { value } }) => {
        if (hasRoleRequiredForEdit) {
            const project_systems = projectSystems.map((item) => _editProductTreeNodeName(item, node.id, value))
            setProjectSystems(project_systems)
        }
    }

    const _removeProductTreeNodeName = (node, id) => ({
        ...node,
        children: node.children
            .filter((item) => item.id !== id)
            .map((item) => _removeProductTreeNodeName(item, id))
    })

    const removeProductTreeNode = (node) => {
        if (hasRoleRequiredForEdit) {
            const project_systems = projectSystems
                .filter((item) => item.id !== node.id)
                .map((item) => _removeProductTreeNodeName(item, node.id))
            setProjectSystems(project_systems)
        }
    }
    
    const _handleChangeProductTreeModels = (node, id, newModels) => ({
        ...node,
        children: node.children.map((item) => _handleChangeProductTreeModels(item, id, newModels)),
        project_system_models: node.id === id ? newModels : node.project_system_models,
    })

    const handleChangeProductTreeModels = (node, selected_models) => {
        if (hasRoleRequiredForEdit) {
            const project_systems = projectSystems.map((item) => _handleChangeProductTreeModels(item, node.id, selected_models))
            setProjectSystems(project_systems)
        }
    }

    const changeProductTree = ({ items }) => {
        if (hasRoleRequiredForEdit) {
            // Changing order
            setProjectSystems(items)
        }
    }

    const hasIsFixed = (item) => {
        const testCurrent = item.project_system_models.some((element) => element.isFixed === true)
        const testChildren = item.children.some((element) => hasIsFixed(element) === true)

        return testCurrent || testChildren
    }

    const setProductTreeCollapse = (value) => {
        localStorage[collapseKey] = value
        if( productTreeRef.current !== null ) {
            productTreeRef.current.collapse(value)
        }
    }
    
    const changeProductTreeCollapse = ({ closedIds }) => {
        setProductTreeCollapse(JSON.stringify(closedIds))
    }
    const productTreeExpandAll = () => {
        const value = "NONE"
        setProductTreeCollapse(value)
    }

    const productTreeCollapseAll = () => {
        const value = "ALL"
        setProductTreeCollapse(value)
    }
    
    const renderProductTreeSystem = ({ item, collapseIcon, handler }) => {
        const readOnly = !hasRoleRequiredForEdit
        const remove = readOnly
                     ? null
                     : hasIsFixed(item)
                     ? (
                         <span>
                             <button type="button" className="btn btn-xs" style={{visibility: 'hidden'}}>
                                 <i className="fa fa-trash" aria-hidden="true"></i>
                             </button>
                         </span>
                     )
                     : item.children.length > 0
                     ? (
                         <Confirm
                             onConfirm={() => removeProductTreeNode(item)}
                             bodyContent="Are you sure you want to remove this item? This item has subitems which will also be removed from the tree. There is no undo."
                             confirmText="Confirm Removing"
                             title="Remove">
                             <button type="button" className="btn btn-xs" data-tooltip-id="gnctooltip" data-tooltip-content="Remove Item">
                                 <i className="fa fa-trash" aria-hidden="true"></i>
                             </button>
                         </Confirm>
                     )
                     : (
                         <span>
                             <button type="button" className="btn btn-xs" data-tooltip-id="gnctooltip" data-tooltip-content="Remove Item" onClick={() => removeProductTreeNode(item)}>
                                 <i className="fa fa-trash" aria-hidden="true"></i>
                             </button>
                         </span>
                     )

        const add = readOnly
                  ? null
                  : (
                      <button type="button" className="btn btn-xs" data-tooltip-id="gnctooltip" data-tooltip-content="Add Subitem" onClick={() => addProductTreeNode(item) }>
                          <i className="fa fa-plus" aria-hidden="true"></i>
                      </button>
                  )

        return (
            <div className="product-tree-container">
                <div className="product-tree-name">
                    <span className="product-tree-handler">{handler}</span>
                    <span className="product-tree-collapse">{collapseIcon}</span>
                    <input className="product-tree-name-text-input" type="text" value={item.name} onChange={(event) => editProductTreeNodeName(item, event) }/>
                </div>
                <div className="product-tree-models">
                    <MultiSelect
                        styleName="productTree"
                        options={models}
                        value={item.project_system_models}
                        onChange={(selected) => handleChangeProductTreeModels(item, selected) }
                        clearable={false}
                        disabled={readOnly}
                    />
                </div>
                <div className="product-tree-actions">
                    {remove}
                    {add}
                </div>
            </div>
        )
    }
    
    const createProductTree = () => {
        const readOnly = !hasRoleRequiredForEdit
        const disableDrag = readOnly ? true : false
        const add = readOnly
                  ? null
                  : (
                      <button type="button" className="btn btn-xs btn-primary" data-tooltip-id="gnctooltip" data-tooltip-content="Add Top-Level Item" onClick={() => addProductTreeRootNode() }>Add</button>
                  )
        const handler = readOnly
                      ? null
                      : (
                          <span className="product-tree-handler"><i className="fa fa-bars" aria-hidden="true"></i></span>
                      )
        return (
            <div>
                <Nestable
                    threshold="25"
                    handler={handler}
                    ref={productTreeRef}
                    onChange={changeProductTree}
                    onCollapseChange={changeProductTreeCollapse}
                    disableCollapse={false}
                    disableDrag={disableDrag}
                    items={projectSystems}
                    renderCollapseIcon={(item) => item.isCollapsed
                                              ? (<i className="fa fa-caret-right" aria-hidden="true"></i>)
                                              : (<i className="fa fa-caret-down" aria-hidden="true"></i>)
                                       }
                    renderItem={renderProductTreeSystem}
                />
                {add}
            </div>
        )
    }

    /* Product Tree stuff END */
    const handleChangeUserAccess = (users) => {
        const updatedUsers = activeUsers.map(user => {
            const selectedUser = users.find(selected => selected.id === user.id)
            const currentProjectAccess = selectedUser?.user_project_access?.find(access => access.project_id === projectID)
                ?? { project_id: projectID, user_project_access_level_id: 1 }
            
            return {
                ...user,
                // Keep all the other projects as they are
                user_project_access: [
                    ...user.user_project_access
                       .filter(access => access.project_id !== projectID),
                    selectedUser ? currentProjectAccess : {},
                ],
            }
        })

        updateAllUsers(updatedUsers)
    }

    const handleChangeUserAccessLevel = (user, levelId) => {
        if (hasRoleRequiredForEdit) {
            const updatedUser = {
                ...user, 
                user_project_access: user.user_project_access.map(project =>
                    project.project_id === projectID
                        ? {
                            ...project,
                            user_project_access_level_id: levelId,
                        } : project
                )
            }
            updateUser(user.id, updatedUser)
        }
    }

    const handleChangeUsergroupAccess = (selected_usergroups) => {
        usergroups.forEach(usergroup => {
            const level = usergroup.usergroup_project_access.find(access => access.project_id === projectID)?.user_project_access_level_id || 1
            
            // 1st, clear access if they have it
            usergroup.usergroup_project_access = usergroup.usergroup_project_access.filter(access => access.project_id !== projectID)
            if( selected_usergroups.find(group => group.id === usergroup.id) ) {
                // Should have access? Go crazy
                usergroup.usergroup_project_access.push({
                    project_id: projectID,
                    user_project_access_level_id: level
                })
            }
            updateUsergroup(usergroup.id, usergroup)
        })
    }

    const handleChangeUsergroupAccessLevel = (usergroup, level_id) => {
        if (hasRoleRequiredForEdit) {
            const updatedUsergroup = {
                ...usergroup,
                usergroup_project_access: usergroup.usergroup_project_access.map(i => {
                    if( i.project_id === projectID ) {
                        i.user_project_access_level_id = level_id
                    }
                    return i
                })
            }
            updateUsergroup(usergroup.id, updatedUsergroup)
        }
    }

    const renderField = ({ field_name: fieldname }) => {
        const globalDisabled = !hasRoleRequiredForEdit
        const requiredIndicator = hasRoleRequiredForEdit ? <label className="form-required-star">&nbsp;*</label> : ''

        const fieldMap = {
            name: (
                <div className="form-group" key="name">
                    <label className="col-sm-2 control-label">Name
                        {requiredIndicator}
                    </label>
                    <div className="col-sm-8">
                        <input className="form-control" type="text" name="name" value={name} onChange={e => setName(e.target.value)} disabled={!hasRoleRequiredForAdminEdit} required />
                    </div>
                </div>
            ),
            description: (
                <div className="form-group" key="description">
                    <label className="col-sm-2 control-label">Description</label>
                    <div className="col-sm-8">
                        <TextArea className="form-control" name="description" value={description} onChange={e => setDescription(e.target.value)} disabled={globalDisabled} />
                    </div>
                </div>
            ),
            project_code: (
                <div className="form-group" key="project_code">
                    <label className="col-sm-2 control-label">Code
                        {!isUsed && requiredIndicator}
                    </label>
                    <div className="col-sm-8">
                        <input className="form-control" type="text" name="project_code"
                               value={projectCode}
                               onChange={e => setProjectCode(e.target.value)} 
                               maxLength="12"
                               disabled={isUsed || !hasRoleRequiredForAdminEdit}
                               required
                        />
                    </div>
                </div>
            ),
            organisation_code: (
                <div className="form-group" key="organisation_code">
                    <label className="col-sm-2 control-label">Organisation Code
                        {!isUsed && requiredIndicator}
                    </label>
                    <div className="col-sm-8">
                        <input className="form-control" type="text" name="organisation_code"
                               value={organisationCode}
                               onChange={e => setOrganisationCode(e.target.value)} 
                               maxLength="4"
                               disabled={isUsed || !hasRoleRequiredForAdminEdit}
                               required
                        />
                    </div>
                </div>
            ),
            element_code: (
                <div className="form-group" key="element_code">
                    <label className="col-sm-2 control-label">Element Code</label>
                    <div className="col-sm-8">
                        <input className="form-control" type="text" name="element_code"
                               value={elementCode}
                               onChange={e => setElementCode(e.target.value)}
                               maxLength="4"
                               disabled={isUsed || !hasRoleRequiredForAdminEdit}
                        />
                    </div>
                </div>
            ),
            project_status_id: (
                <div className="form-group" key="project_status_id">
                    <label className="col-sm-2 control-label">Status</label>
                    <div className="col-sm-8">
                        <SingleSelect
                            value={projectStatusID}
                            onChange={value => setProjectStatusID(value.id)} 
                            options={projectStatuses}
                            disabled={globalDisabled}
                        />
                    </div>
                </div>
            ),
            project_manager_user_id: (
                <UserSelect
                    key="project_manager_user_id"
                    label="Manager"
                    value={projectManagerUserID}
                    onChange={value => setProjectManagerUserID(value)}
                    disabled={!hasRoleRequiredForEdit}
                />
            ),
            pa_manager_user_id: (
                <UserSelect
                    key="pa_manager_user_id"
                    label="PA Manager"
                    value={paManagerUserID}
                    onChange={value => setPaManagerUserID(value)}
                    disabled={!hasRoleRequiredForEdit}
                />
            ),
            technical_manager_user_id: (
                <UserSelect
                    key="technical_manager_user_id"
                    label="Technical Manager"
                    value={technicalManagerUserID}
                    onChange={value => setTechnicalManagerUserID(value)}
                    disabled={!hasRoleRequiredForEdit}
                />
            ),
            bid_manager_user_id: (
                <UserSelect
                    key="bid_manager_user_id"
                    label="Bid Manager"
                    name="bid_manager_user_id"
                    value={bidManagerUserID}
                    onChange={value => setBidManagerUserID(value)}
                    disabled={!hasRoleRequiredForEdit}
                />
            ),
            project_configuration_scheme_id: (
                <div className="form-group" key="project_configuration_scheme_id">
                    <label className="col-sm-2 control-label">Configuration Scheme
                        {!isUsed && requiredIndicator}
                    </label>
                    <div className="col-sm-8">
                        <SingleSelect
                            value={projectConfigurationSchemeID}
                            onChange={value => setProjectConfigurationSchemeID(value.id)}
                            options={projectConfigurationSchemes}
                            disabled={isUsed || !hasRoleRequiredForAdminEdit}
                        />
                    </div>
                </div>
            ),
            project_systems: (
                <div key="project_systems">
                    <div className="hr-line-dashed"></div>
                    
                    <div className="form-group">
                        <label className="col-sm-2"></label>
                        <div className="col-sm-8">
                            <h3 className="stonly product-tree-header">Product Tree </h3>
                            <div className="btn-group" style={{float: "right"}}>
                                <button type="button" className="btn btn-white btn-xs" onClick={productTreeExpandAll}>
                                    Expand All
                                </button>
                                <button type="button" className="btn btn-white btn-xs" onClick={productTreeCollapseAll}>
                                    Collapse All
                                </button>
                            </div>
                            <p>This {projectType.name} is configured according to the Product Tree below.</p>
                        </div>
                    </div>
                    
                    <div className="form-group">
                        <label className="col-sm-2"></label>
                        <div className="col-sm-8">
                            {createProductTree()}
                        </div>
                    </div>
                </div>
            ),
            do_allow_document_sharing: (
                <div key="allow_sharing_div">
                    <div className="hr-line-dashed"></div>

                    <div className="form-group" style={{marginBottom: 0}}>
                        <label className="col-sm-2"></label>
                        <div className="col-sm-8">
                            <h3>Sharing</h3>
                            <p>Shared documents can be Included in other Projects or Products, and viewed by their members.</p>
                            
                            <div className="form-check" key="do-allow-sharing">
                                <Checkbox
                                    id="do-allow-sharing"
                                    name="do-allow-sharing"
                                    onChange={() => setDoAllowDocumentSharing(!doAllowDocumentSharing)}
                                    isChecked={doAllowDocumentSharing}
                                    disabled={globalDisabled}
                                />
                                <label htmlFor="do-allow-sharing">
                                    Allow Document Sharing
                                </label>
                            </div>
                            
                            <br/>
                            
                            <label className="control-label">This {projectType.name} is sharing Documents with:</label>

                            <ul className="list-group clear-list m-t">
                                {incorporatedInProjects.length === 0 &&
                                 <li className="list-group-item">
                                     No Projects or Products
                                 </li>
                                }
                                {incorporatedInProjects.map(function(item) {
                                     return userHasExplicitProjectAccess( currentUser, item.id )
                                          ? (
                                              <li key={item.id} className="list-group-item">
                                                  <Link to={`/portfolio/${item.id}/dashboard`}>
                                                      {item.name}
                                                  </Link>
                                              </li>
                                          )
                                          : (
                                              <li key={item.id} className="list-group-item">
                                                  {item.name}
                                              </li>
                                          )
                                     
                                 })}
                            </ul>
                        </div>
                    </div>
                </div>
            ),
            // This is for OSS opportunity
            customer: (<TextField key="customer" label="Customer" fieldName="customer" value={customer} onChange={e => setCustomer(e.target.value)} disabled={globalDisabled} />),
            funding_source: (<TextField key="funding_source" label="Funding Source" fieldName="funding_source" value={fundingSource} onChange={e => setFundingSource(e.target.value)} disabled={globalDisabled} />),
            funding_type: (
                <div className="form-group" key="funding_type">
                    <label className="col-sm-2 control-label">Funding Type</label>
                    <div className="col-sm-8">
                        <SingleSelect
                            value={fundingTypeID}
                            onChange={item => setFundingTypeID(item.id)}
                            options={projectFundingTypes}
                            disabled={!hasRoleRequiredForEdit}
                        />
                    </div>
                </div>
            ),
            funding_level: (<TextField key="funding_level" label="Funding Level" fieldName="funding_level" value={fundingLevel} onChange={e => setFundingLevel(e.target.value)} disabled={globalDisabled} />),
            product: (<TextField key="product" label="Product" fieldName="product" value={product} onChange={e => setProduct(e.target.value)} disabled={globalDisabled} />),
            total_value: (<TextField key="total_value" label="Total Value" fieldName="total_value" value={totalValue} onChange={e => setTotalValue(e.target.value)} disabled={globalDisabled} />)
        }

        return fieldMap[fieldname] ?? <h1 key={fieldname}>{fieldname}</h1>
    }

    const actionConvertToProduct = () => {
        const data = { project_type_id: 3 }

        fetch(projectURL, {
            credentials: 'same-origin',
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        })
        .then(function(response) {
            return response.json()
        })
        .then(function(json) {
            dispatch(setProject(json.result))
            reloadProjects()
            goBack()
        })
    }
    
    const actionConvertToProject = () => {
        const data = { project_type_id: 1 }

        fetch(projectURL, {
            credentials: 'same-origin',
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        })
            .then(function(response) {
                return response.json()
            })
            .then(function(json) {
                dispatch(setProject(json.result))
                reloadProjects()
                goBack()
            })
    }

    const renderArchiveButton = () => {
        const canArchive = id && ! isArchived && hasRoleRequiredForEdit
        if(canArchive) {
            return (
                <Confirm
                    title="Archive"
                    bodyContent="Are you sure you want to archive this? Archiving will remove all User Access and User Group Access."
                    confirmText="Confirm Archiving"
                    onConfirm={() => handleArchive()}
                >
                    <button key="archive-button" className="btn btn-link pull-right" type="button"
                            data-tooltip-id="gnctooltip" data-tooltip-content="Archive">
                        <i className="fa fa-archive" aria-hidden="true"></i>
                    </button>
                </Confirm>
            )
        }
    }
    
    const renderConvertButton = () => {
        if( ! id ) {
            return null
        }
        if( projectTypeID === 2 ) { // Opportunity
            return null
        }

        // If both roles, give actual actionable buttons
        if(userHasRole(currentUser,'project_admin') && userHasRole(currentUser,'product_admin')) {
            if( projectType.name === 'Project' ) {
                return (
                    <Confirm
                        title='Convert'
                        bodyContent="Are you sure you want to convert this Project to a Product?"
                        confirmText='Confirm Convert'
                        onConfirm={() => actionConvertToProduct()}
                    >
                        <button key="convert-button" className="btn btn-link pull-right" type="button"
                                data-tooltip-id="gnctooltip" data-tooltip-content="Convert this Project to a Product">
                            <i className="fa fa-exchange" aria-hidden="true"></i>
                        </button>
                    </Confirm>
                )
            }
            if( projectType.name === 'Product' ) {
                return (
                    <Confirm
                        title='Convert'
                        bodyContent="Are you sure you want to convert this Product to a Project?"
                        confirmText='Confirm Convert'
                        onConfirm={() => actionConvertToProject()}
                    >
                        <button key="convert-button" className="btn btn-link pull-right" type="button"
                                data-tooltip-id="gnctooltip" data-tooltip-content="Convert this Product to a Project">
                            <i className="fa fa-exchange" aria-hidden="true"></i>
                        </button>
                    </Confirm>
                )
            }
        }
        // or, if only one, give a teaser button (showing info)
        if(userHasRole(currentUser,'project_admin') || userHasRole(currentUser,'product_admin')) {
            return (
                <span>
                    <InfoModal
                        title="Converting between Project and Product"
                        titleIcon="fa fa-info-circle"
                        bodyContent="Converting requires both Project Admin and Product Admin roles."
                        show={showConvertingModal}
                        onClose={() => setShowConvertingModal(false)}
                    />
                    <button key="convert-button" className="btn btn-link pull-right" type="button"
                            data-tooltip-id="gnctooltip" data-tooltip-content="Convert this Product to a Project"
                            onClick={() => setShowConvertingModal(true)}
                    >
                        <i className="fa fa-exchange" aria-hidden="true"></i>
                    </button>
                </span>
            )
        }
        return null
    }

    const renderUserAccess = () => {
        if( id && isArchived ) {
            return (
                <div className="form-group" style={{marginBottom: 0}}>
                    <label className="col-sm-2"></label>
                    <div className="col-sm-8">
                        <h3 className="users-header">User Access</h3>
                        <p>This Portfolio item has been archived. Unarchive to add Users.</p>
                    </div>
                </div>
            )
        }
        
        return (
            <>
            <div key="user-access" className="form-group" style={{marginBottom: 0}}>
                <label className="col-sm-2"></label>
                <div className="col-sm-8">
                    <h3 className="users-header">User Access</h3>
                    <p>All users below will be able to access this.</p>
                    {hasRoleRequiredForEdit &&
                     <div className="btn-group" style={{float: 'right'}}>
                         <Dropdown>
                             <Dropdown.Toggle size="xs" variant="white">
                                 Add All As <b className="caret"></b>
                             </Dropdown.Toggle>

                             <Dropdown.Menu>
                                 {projectAccessLevels.map(function (i) {
                                      return (
                                          <Dropdown.Item key={i.code}>
                                              <a className="dropdown-item" onClick={() => addAllUsers(i.id)}>
                                                  {i.name}
                                              </a>
                                          </Dropdown.Item>
                                      )
                                  })
                                 }
                             </Dropdown.Menu>
                         </Dropdown>
                         <button type="button" className="btn btn-white btn-xs"
                                 onClick={removeAllUsers}>Remove All
                         </button>
                     </div>
                    }
                </div>
            </div>

            {hasRoleRequiredForEdit && activeUsers ?
                <div key="handle-change-user-access" className="form-group">
                    <label className="col-sm-2 control-label"></label>
                    <div className="col-sm-8">
                        <MultiSelect
                            options={activeUsers}
                            value={selectedUsers}
                            onChange={handleChangeUserAccess}
                            clearable={false}
                        />
                    </div>
                </div>
            : null}

            <div key="selected-users" className="form-group">
                <div className="col-sm-2" />
                <div className="col-sm-8">
                    <table className="table">
                        <tbody>
                            {selectedUsers.map((item) => {
                                 return (
                                     <tr key={item.id}>
                                         <td>
                                             {item.forename} {item.surname}
                                             <AsyncContentModal
                                                 requestURL={`/v0/users/${item.id}`}
                                                 contentCb={userModalContent}
                                             />
                                         </td>
                                         <td>
                                             <div className="btn-group">
                                                {projectAccessLevels.map(level => {
                                                    const className = userProjectAccessLevel(item, projectID) === level.id ? 'btn btn-sm btn-primary' : 'btn btn-sm btn-white'
                                                    return (
                                                        <button
                                                            className={className}
                                                            type="button"
                                                            key={level.id}
                                                            value={level.id}
                                                            onClick={() => handleChangeUserAccessLevel(item, level.id)}
                                                            data-tooltip-id="gnctooltip" data-tooltip-content={level.tooltip}
                                                        >
                                                            {level.name}
                                                        </button>
                                                      )
                                                })}
                                             </div>
                                         </td>
                                     </tr>
                                 )
                             })
                            }
                        </tbody>
                    </table>
                </div>
            </div>
            </>
        )
    }

    const usergroupModalContent = (usergroupData) => {
        const usergroup = usergroupData.result
        const count = usergroup.users ? usergroup.users.length : 0
        const members = usergroup.users && usergroup.users.length > 0
                      ? usergroup.users.map(u => u.forename + ' ' + u.surname)
                      : [ 'None' ]
        return (
            <div>
                <h3>{usergroup.name}</h3>
                <p>{usergroup.description}</p>
                <p>&nbsp;</p>
                <h4>User Group Members ({count})</h4>
                <p>{members.join(', ')}.</p>
            </div>
        )
    }
    
    const userModalContent = (userData) => {
        const u = userData.result
        const groups = u.usergroups && u.usergroups.length > 0
                     ? u.usergroups.map(group => group.name)
                     : [ 'None' ]
        return (
            <div>
                <h3>{u.forename} {u.surname}</h3>
                <p>&nbsp;</p>
                <h4>User Group Membership</h4>
                <p>{groups.join(', ')}.</p>
            </div>
        )
    }
    
    const renderUsergroupAccess = () => {
        if( id && isArchived ) {
            return (
                <div className="form-group" style={{marginBottom: 0}}>
                    <label className="col-sm-2"></label>
                    <div className="col-sm-8">
                        <h3 className="usergroups-header">User Group Access</h3>
                        <p>This Portfolio item has been archived. Unarchive to add User Groups.</p>
                    </div>
                </div>
            )
        }
        
        return <>
            <div key="usergroups-access" className="form-group" style={{marginBottom: 0}}>
                <label className="col-sm-2"></label>
                <div className="col-sm-8">
                    <h3 className="usergroups-header">User Group Access</h3>
                    <p>All users in the user groups below will be able to access this.</p>
                    {hasRoleRequiredForEdit &&
                     <div className="btn-group" style={{float: 'right'}}>
                         <Dropdown>
                             <Dropdown.Toggle size="xs" variant="white">
                                 Add All As <b className="caret"></b>
                             </Dropdown.Toggle>

                             <Dropdown.Menu>
                                 {projectAccessLevels.map(function (i) {
                                      return (
                                          <Dropdown.Item key={i.code}>
                                              <a className="dropdown-item" onClick={() => addAllUsergroups(i.id)}>
                                                  {i.name}
                                              </a>
                                          </Dropdown.Item>
                                      )
                                  })
                                 }
                             </Dropdown.Menu>
                         </Dropdown>
                         <button type="button" className="btn btn-white btn-xs"
                                 onClick={removeAllUsergroups}>Remove All
                         </button>
                     </div>
                    }
                </div>
            </div>
        
            {hasRoleRequiredForEdit && usergroups ?
                <div key="handle-change-usergroup-access" className="form-group">
                    <label className="col-sm-2 control-label"></label>
                    <div className="col-sm-8">
                        <MultiSelect
                            options={usergroups}
                            value={selectedUsergroups}
                            onChange={handleChangeUsergroupAccess}
                            clearable={false}
                        />
                    </div>
                </div>
            : null}

            <div key="selected-usergroups" className="form-group">
                <label className="col-sm-2 control-label"></label>
                
                <div className="col-sm-8">
                    <table className="table">
                        <tbody>
                            {selectedUsergroups.map(function(item) {
                                 return (
                                     <tr key={item.id}>
                                         <td>
                                             {item.name}
                                             
                                             <AsyncContentModal
                                                 requestURL={`/v0/usergroups/${item.id}`}
                                                 contentCb={usergroupModalContent}
                                             />
                                         </td>
                                         <td>
                                             <div className="btn-group">
                                                 {projectAccessLevels.map(function (i) {
                                                      let className = ( usergroupProjectAccessLevel(item, projectID) === i.id ) ? 'btn btn-sm btn-primary' : 'btn btn-sm btn-white'
                                                      return (
                                                          <button
                                                              className={className}
                                                              type="button"
                                                              key={i.id}
                                                              value={i.id}
                                                              onClick={() => handleChangeUsergroupAccessLevel(item, i.id)}
                                                              data-tooltip-id="gnctooltip" data-tooltip-content={i.tooltip}
                                                          >
                                                              {i.name}
                                                          </button>
                                                      )
                                                  })
                                                 }
                                             </div>
                                         </td>
                                     </tr>
                                 )
                             })
                            }
                        </tbody>
                    </table>
                </div>
            </div>
        </>
    }

    const newLabel = projectType && projectType.name ? `<New ${projectType.name}>` : '<New Project>'
    const canDelete = !isUsed && id && hasRoleRequiredForAdminEdit
    const canUnarchive = id && isArchived && hasRoleRequiredForEdit
    
    const convertButton = renderConvertButton()
    const archiveButton = renderArchiveButton()
    const userAccess = renderUserAccess()
    const usergroupAccess = renderUsergroupAccess()
    
    if (!hasLoaded) {
        return <Loading />
    }
    
    return (
        <div className="wrapper wrapper-content">
            <div className="row">
                <div className="col-lg-12">
                    <div className="ibox float-e-margins">
                        <div className="ibox-title">
                            <h2>
                                {name || newLabel}
                            </h2>
                        </div>
                        <div className="ibox-content">
                            <form className="form-horizontal" onSubmit={(event) => handleSubmit(event)}>

                                {projectType.fields.map(renderField)}

                                <div>&nbsp;</div>
                                
                                <div className="hr-line-dashed"></div>
                                
                                {userAccess}
                                
                                <div className="hr-line-dashed"></div>
                                
                                {usergroupAccess}
                                
                                <div className="hr-line-dashed"></div>

                                <div className="form-group">
                                    <ErrorMessage
                                        error={error}
                                    />
                                    
                                    <div className="col-sm-8 col-sm-offset-2">
                                        { hasRoleRequiredForEdit &&
                                          <CancelButton />
                                        }
            
            <SaveButton
                buttonVariant='primary'
                visible={hasRoleRequiredForEdit}
                spinnerActive={submitSpinner}
                label='Save Changes'
                onClick={handleSubmit}
                onError={handleSubmitError}
            />

            <DeleteButton
                url={projectURL}
                onError={handleSubmitError}
                isHidden={!canDelete}
                callback={() => { projectDelete(projectID); dispatch(setIsDeleted(true)) }}
            />
            
                                        { canUnarchive &&
                                          <Confirm
                                              title='Unarchive'
                                              bodyContent="Are you sure you want to unarchive this?"
                                              confirmText='Confirm Unarchiving'
                                              onConfirm={() => handleUnarchive()}
                                          >
                                                  <button type="button" className="btn btn-warning btn-mr">Unarchive</button>
                                              </Confirm>
                                        }

                                        {archiveButton}
                                        {convertButton}
            
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Project
